export type Entity = number;

/** Typed component map. Iteration order is insertion order. */
export class ComponentStore<T> {
  private readonly map = new Map<Entity, T>();
  get(e: Entity): T | undefined { return this.map.get(e); }
  /** Non-null get; throws if missing. Use when the component is guaranteed by construction. */
  req(e: Entity): T {
    const c = this.map.get(e);
    if (c === undefined) throw new Error(`entity ${e} lacks component`);
    return c;
  }
  set(e: Entity, c: T): T { this.map.set(e, c); return c; }
  has(e: Entity): boolean { return this.map.has(e); }
  delete(e: Entity): boolean { return this.map.delete(e); }
  get size(): number { return this.map.size; }
  entries(): IterableIterator<[Entity, T]> { return this.map.entries(); }
  values(): IterableIterator<T> { return this.map.values(); }
  keys(): IterableIterator<Entity> { return this.map.keys(); }
  [Symbol.iterator](): IterableIterator<[Entity, T]> { return this.map.entries(); }
  clear(): void { this.map.clear(); }
}

/**
 * Minimal ECS: an entity is an integer id; components live in typed stores
 * registered with the scene so destroy() can sweep them.
 */
export class Scene {
  private nextId = 1;
  readonly alive = new Set<Entity>();
  private readonly stores: ComponentStore<unknown>[] = [];
  private readonly pendingDestroy: Entity[] = [];
  /** Optional per-entity debug names. */
  readonly names = new Map<Entity, string>();

  store<T>(): ComponentStore<T> {
    const s = new ComponentStore<T>();
    this.stores.push(s as ComponentStore<unknown>);
    return s;
  }
  create(name?: string): Entity {
    const e = this.nextId++;
    this.alive.add(e);
    if (name) this.names.set(e, name);
    return e;
  }
  isAlive(e: Entity): boolean { return this.alive.has(e); }
  /** Deferred: entity is removed at the next flush() (end of sim step). */
  destroy(e: Entity): void { if (this.alive.has(e)) this.pendingDestroy.push(e); }
  destroyNow(e: Entity): void {
    this.alive.delete(e);
    this.names.delete(e);
    for (const s of this.stores) s.delete(e);
  }
  flush(): void {
    for (const e of this.pendingDestroy) this.destroyNow(e);
    this.pendingDestroy.length = 0;
  }
  clear(): void {
    for (const s of this.stores) s.clear();
    this.alive.clear();
    this.names.clear();
    this.pendingDestroy.length = 0;
  }
}
